import "./styles/main.css";

const ready = (callback: () => void) => {
  if (document.readyState !== "loading") {
    callback();
  } else {
    document.addEventListener("DOMContentLoaded", callback);
  }
};

ready(() => {
  enhanceHeader();
  bindMobileNavigation();
  enableSmoothAnchors();
  activateStatCounters();
  setupGalleryToggle();
});

function enhanceHeader() {
  const header = document.querySelector<HTMLElement>("[data-site-header]");
  const sentinel = document.querySelector<HTMLElement>("[data-hero]");

  if (!header) {
    return;
  }

  // If no hero section exists on the page, always show the header background
  if (!sentinel) {
    header.dataset.scrolled = "true";
    return;
  }

  // Update header background based on scroll position
  const updateHeader = () => {
    if (window.scrollY > 50) {
      header.dataset.scrolled = "true";
    } else {
      header.dataset.scrolled = "false";
    }
  };

  // Check on load
  updateHeader();

  // Update on scroll
  window.addEventListener("scroll", updateHeader, { passive: true });
}

function bindMobileNavigation() {
  const toggle = document.querySelector<HTMLButtonElement>("[data-mobile-toggle]");
  const target = document.querySelector<HTMLElement>("[data-mobile-menu]");

  if (!toggle || !target) {
    return;
  }

  toggle.addEventListener("click", () => {
    const expanded = toggle.getAttribute("aria-expanded") === "true";
    toggle.setAttribute("aria-expanded", (!expanded).toString());
    target.dataset.open = (!expanded).toString();
  });
}

function enableSmoothAnchors() {
  const links = document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]');

  links.forEach((link) => {
    link.addEventListener("click", (event) => {
      const href = link.getAttribute("href");
      if (!href || href.length < 2) {
        return;
      }

      const target = document.querySelector<HTMLElement>(href);
      if (!target) {
        return;
      }

      event.preventDefault();
      const top = target.getBoundingClientRect().top + window.pageYOffset - 96;
      window.scrollTo({ top, behavior: "smooth" });
    });
  });
}

function activateStatCounters() {
  const counters = document.querySelectorAll<HTMLElement>("[data-animate-number]");

  if (!counters.length || !("IntersectionObserver" in window)) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const el = entry.target as HTMLElement;
        const targetValue = Number(el.dataset.animateNumber);
        if (!Number.isFinite(targetValue)) {
          obs.unobserve(el);
          return;
        }

        animateValue(el, targetValue);
        obs.unobserve(el);
      });
    },
    { threshold: 0.4 }
  );

  counters.forEach((counter) => observer.observe(counter));
}

function animateValue(element: HTMLElement, target: number) {
  const duration = 1200;
  const frameRate = 1000 / 60;
  const totalFrames = Math.round(duration / frameRate);
  let frame = 0;

  const countTo = Number(target);
  const countFrom = Number(element.innerText.replace(/[^0-9.]/g, "")) || 0;
  const easeOutQuart = (t: number) => 1 - Math.pow(1 - t, 4);

  const counter = setInterval(() => {
    frame += 1;
    const progress = easeOutQuart(frame / totalFrames);
    const currentValue = Math.round(countFrom + (countTo - countFrom) * progress);
    element.innerText = currentValue.toString();

    if (frame === totalFrames) {
      clearInterval(counter);
      element.innerText = countTo.toString();
    }
  }, frameRate);
}

function setupGalleryToggle() {
  const controls = document.querySelectorAll<HTMLButtonElement>("[data-gallery-filter]");
  const items = document.querySelectorAll<HTMLElement>("[data-gallery-item]");

  if (!controls.length || !items.length) {
    return;
  }

  controls.forEach((control) => {
    control.addEventListener("click", () => {
      const category = control.dataset.galleryFilter;

      controls.forEach((button) => button.classList.toggle("is-active", button === control));
      items.forEach((item) => {
        const categories = item.dataset.galleryCategory?.split(",") ?? [];
        const match = !category || category === "all" || categories.includes(category);
        item.dataset.visible = match.toString();
      });
    });
  });
}
